/**
 * データフィルタリングロジック
 *
 * 作品とイベントデータのフィルタリング機能を提供します。
 * ジャンル・発表形態・キーワード・制作年などの条件でフィルタリングします。
 */

import type {
  Work,
  Event,
  TimelineItem,
  ChronologyEntry,
} from '@/types/domain';
import { getLocalizedText } from './i18n-helpers';
import { getTimelineItemGenre } from './timeline-data';
import { compareDates, extractYear } from './date-formatters';

/**
 * アイテム配列からユニークな値を抽出するジェネリックヘルパー
 *
 * @param items - 対象アイテムの配列
 * @param getValue - 各アイテムから値を取得する関数（単値または配列）
 * @returns ユニークな値の配列（ソート済み）
 */
function extractUniqueValues<T>(
  items: T[],
  getValue: (item: T) => string | string[] | undefined
): string[] {
  const set = new Set<string>();
  items.forEach((item) => {
    const value = getValue(item);
    if (Array.isArray(value)) {
      value.forEach((v) => set.add(v));
    } else if (value) {
      set.add(value);
    }
  });
  return Array.from(set).sort();
}

export const KEYWORD_FILTER_ALLOWLIST = new Set([
  '作品集',
  '出演',
  'おすすめ',
  '驚き盤',
  '広告',
]);

/**
 * フィルタ条件の型（アクションメソッドを除いたデータのみ）
 */
export interface FilterCriteria {
  /** 選択されたジャンル（schema:genre） */
  genres: string[];

  /** 選択された発表形態 */
  artforms: string[];

  /** 選択されたキーワード（schema:keyword） */
  keywords: string[];

  /** 選択されたグループ */
  groups: string[];

  /** 制作年の範囲 [開始年, 終了年] */
  dateRange: [number, number];

  /** 検索クエリ */
  searchQuery: string;
}

/**
 * 作品をフィルタリング
 *
 * FilterStateの条件に基づいて作品をフィルタリングします。
 * 複数の条件はAND条件で結合されます。
 * 同一カテゴリ内の複数選択はOR条件です。
 *
 * @param works - 作品データの配列
 * @param filters - フィルタ条件
 * @param _language - 言語設定（将来の多言語対応用）
 * @returns フィルタリングされた作品配列
 */
export function filterWorks(
  works: Work[],
  filters: FilterCriteria,
  _language: 'ja' | 'en' = 'ja'
): Work[] {
  return works.filter((work) => {
    // ジャンルフィルタ（OR条件）
    if (filters.genres.length > 0) {
      const genre = work.genre;
      if (!genre || !filters.genres.includes(genre)) {
        return false;
      }
    }

    // 発表形態フィルタ（OR条件）
    if (filters.artforms.length > 0) {
      const artform = work.presentation;
      if (!artform || !filters.artforms.includes(artform)) {
        return false;
      }
    }

    // キーワードフィルタ（OR条件）
    if (filters.keywords.length > 0) {
      const workKeyword = work.keywords;
      const keywordArray = Array.isArray(workKeyword)
        ? workKeyword
        : workKeyword
          ? [workKeyword]
          : [];

      // いずれかのキーワードが含まれていればOK
      if (!filters.keywords.some((k) => keywordArray.includes(k))) {
        return false;
      }
    }

    // グループフィルタ（OR条件）
    if (filters.groups.length > 0) {
      const workGroup = work.group;
      const groupArray = Array.isArray(workGroup)
        ? workGroup
        : workGroup
          ? [workGroup]
          : [];

      if (!filters.groups.some((g) => groupArray.includes(g))) {
        return false;
      }
    }

    // 制作年フィルタ
    if (work.dateCreated) {
      const createdYear = parseInt(work.dateCreated, 10);

      if (
        !isNaN(createdYear) &&
        (createdYear < filters.dateRange[0] ||
          createdYear > filters.dateRange[1])
      ) {
        return false;
      }
    }

    return true;
  });
}

/**
 * イベントをフィルタリング
 *
 * FilterStateの条件に基づいてイベントをフィルタリングします。
 * 複数の条件はAND条件で結合されます。
 *
 * @param events - イベントデータの配列
 * @param filters - フィルタ条件
 * @param language - 言語設定（多言語フィールドの比較に使用）
 * @returns フィルタリングされたイベント配列
 */
export function filterEvents(
  events: Event[],
  filters: FilterCriteria,
  _language: 'ja' | 'en' = 'ja'
): Event[] {
  return events.filter((event) => {
    // ジャンルフィルタ
    if (filters.genres.length > 0) {
      const eventGenre = event.eventType;
      if (!eventGenre || !filters.genres.includes(eventGenre)) {
        return false;
      }
    }

    // 日付範囲フィルタ（開始日で判定）
    if (event.startDate) {
      const startYear = extractYear(event.startDate);

      if (
        startYear !== null &&
        (startYear < filters.dateRange[0] || startYear > filters.dateRange[1])
      ) {
        return false;
      }
    }

    return true;
  });
}

/**
 * 作品から利用可能なジャンル一覧を抽出
 *
 * @param works - 作品データの配列
 * @returns ユニークなジャンルの配列（ソート済み）
 */
export function extractGenresFromWorks(works: Work[]): string[] {
  return extractUniqueValues(works, (w) => w.genre);
}

/**
 * 作品から利用可能な発表形態一覧を抽出
 *
 * @param works - 作品データの配列
 * @returns ユニークな発表形態の配列（ソート済み）
 */
export function extractArtformsFromWorks(works: Work[]): string[] {
  return extractUniqueValues(works, (w) => w.presentation);
}

/**
 * 作品から利用可能なキーワード一覧を抽出
 *
 * @param works - 作品データの配列
 * @returns ユニークなキーワードの配列（ソート済み）
 */
export function extractKeywordsFromWorks(works: Work[]): string[] {
  return extractUniqueValues(works, (w) => w.keywords);
}

/**
 * 作品から利用可能なグループ一覧を抽出
 *
 * @param works - 作品データの配列
 * @returns ユニークなグループの配列（ソート済み）
 */
export function extractGroupsFromWorks(works: Work[]): string[] {
  return extractUniqueValues(works, (w) => w.group);
}

/**
 * イベントから利用可能なジャンル一覧を抽出
 *
 * @param events - イベントデータの配列
 * @param language - 言語設定
 * @returns ユニークなジャンルの配列（ソート済み）
 */
export function extractGenresFromEvents(
  events: Event[],
  _language: 'ja' | 'en' = 'ja'
): string[] {
  return extractUniqueValues(events, (e) => e.eventType);
}

/**
 * 作品とイベントから制作年・開催年の範囲を取得
 *
 * @param works - 作品データの配列
 * @param events - イベントデータの配列
 * @returns [最小年, 最大年]
 */
export function getDateRange(works: Work[], events: Event[]): [number, number] {
  const years: number[] = [];

  // 作品の制作年を収集
  works.forEach((work) => {
    if (work.dateCreated) {
      const year = parseInt(work.dateCreated, 10);
      if (!isNaN(year)) {
        years.push(year);
      }
    }
  });

  // イベントの開始年を収集
  events.forEach((event) => {
    if (event.startDate) {
      const year = extractYear(event.startDate);
      if (year !== null) {
        years.push(year);
      }
    }
  });

  if (years.length === 0) {
    // データがない場合のデフォルト範囲
    return [1960, new Date().getFullYear()];
  }

  return [Math.min(...years), Math.max(...years)];
}

/**
 * フィルタ条件がデフォルト状態かどうかを判定
 *
 * @param filters - フィルタ条件
 * @param defaultDateRange - デフォルトの日付範囲
 * @returns フィルタがデフォルト状態ならtrue
 */
export function isDefaultFilter(
  filters: FilterCriteria,
  defaultDateRange: [number, number]
): boolean {
  return (
    filters.genres.length === 0 &&
    filters.artforms.length === 0 &&
    filters.keywords.length === 0 &&
    filters.groups.length === 0 &&
    filters.dateRange[0] === defaultDateRange[0] &&
    filters.dateRange[1] === defaultDateRange[1]
  );
}

/**
 * タイムライン用フィルタ条件の型
 */
export interface TimelineFilterCriteria {
  /** 選択されたジャンル */
  genres: string[];

  /** 表示する年代範囲（10年単位の開始年。例: [1960, 2020] = 1960年代〜2020年代） */
  decadeRange: [number, number];

  /** 検索クエリ */
  searchQuery: string;
}

/**
 * タイムラインアイテムをフィルタリング
 *
 * @param items - タイムラインアイテムの配列
 * @param filters - フィルタ条件
 * @param language - 言語設定（ジャンル比較・検索クエリの比較に使用）
 * @returns フィルタリングされ、日付順にソートされたタイムラインアイテム配列
 */
export function filterTimelineItems(
  items: TimelineItem[],
  filters: TimelineFilterCriteria,
  language: 'ja' | 'en' = 'ja'
): TimelineItem[] {
  const filtered = items.filter((item) => {
    // ジャンルフィルタ（選択されている場合のみ適用）
    if (filters.genres.length > 0) {
      const genre = getTimelineItemGenre(item, language);
      if (!genre || !filters.genres.includes(genre)) {
        return false;
      }
    }

    // 年代範囲フィルタ（10年単位）
    if (item.date) {
      const year = extractYear(item.date);

      if (year !== null) {
        // 開始10年代: 1960 = 1960〜1969
        // 終了10年代: 2020 = 2020〜2029
        const startYear = filters.decadeRange[0];
        const endYear = filters.decadeRange[1] + 9;

        if (year < startYear || year > endYear) {
          return false;
        }
      }
    }

    // 検索クエリフィルタ
    if (filters.searchQuery.trim()) {
      const title = getLocalizedText(item.title, language).toLowerCase();
      const query = filters.searchQuery.toLowerCase();

      if (!title.includes(query)) {
        return false;
      }
    }

    return true;
  });

  // フィルタ後に日付順でソート（昇順、年代範囲は明示的な年の後に配置）
  return filtered.sort((a, b) => compareDates(a.date, b.date));
}

/**
 * 年表エントリ（chronology）を年代範囲でフィルタリング
 *
 * @param entries - 年表エントリの配列
 * @param decadeRange - 表示する年代範囲（10年単位の開始年。例: [1960, 2020] = 1960年代〜2020年代）
 * @returns フィルタリングされた年表エントリ配列
 */
export function filterChronologyEntries(
  entries: ChronologyEntry[],
  decadeRange: [number, number]
): ChronologyEntry[] {
  return entries.filter((entry) => {
    // 日付がない場合はスキップ
    if (!entry.date) {
      return false;
    }

    const year = extractYear(entry.date);

    if (year !== null) {
      // 開始10年代: 1960 = 1960〜1969
      // 終了10年代: 2020 = 2020〜2029
      const startYear = decadeRange[0];
      const endYear = decadeRange[1] + 9;

      if (year < startYear || year > endYear) {
        return false;
      }
    }

    return true;
  });
}
